import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import PropTypes from "prop-types";
import {useDispatch, useSelector} from "react-redux";
import {loadPartnerProjectsWithNew, saveProject} from "../../../../actions/projectsActions";
import LinksCreationTable from "./LinksCreationTable";
import MaterialUISelect from "../../../common/selects/MaterialUISelect";
import ProjectCreationCollapse from "../../../common/projectCreation/ProjectCreationCollapse";
import SmartLinksCreationTable from "./SmartLinksCreationTable";
import PageContent from "../../../common/page/PageContent";
import OfferLinkInfo from "../OfferLinkInfo";
import {PARTNER_MACROS} from "../../../../utils/constants";
import {loadUserPresets} from "../../../../actions/presetActions";
import MacrosCollapse from "../macros/MacrosCollapse";
import {presetsAccessType} from "../../../../utils/presetsUtils";
import PostbackTestModal from "../../projects/modal/PostbackTestModal";
import {sendPostbackTest} from "../../../../actions/postbackTestActions";
import {loadPromotionLinks} from "../../../../actions/partnerOfferActions";
import {billingTypesData} from "../../../../utils/offerUtils";


function getInitialWidth() {
    return typeof window === 'undefined' ? 0 : window.innerWidth;
}

function getContentWidth(container) {
    if (container && container.offsetWidth) {
        return container.offsetWidth;
    }

    return getInitialWidth();
}

function buildBillingTypeDropdown(selectedOffers) {
    let billingTypes = selectedOffers?.flatMap(offer => offer.offerIncomes.map(income => income.billingType));
    billingTypes = billingTypes ? [...new Set(billingTypes)] : [];
    return billingTypesData.filter(dataItem => billingTypes.includes(dataItem.type));
}

function ReturnButton({onClick}) {
    return (
        <button className="circle-btn" title="Назад" onClick={onClick}>
            <i className="ti ti-back-left"/>
        </button>
    );
}

function LinkCreationActions({isFormExpanded, offersBtnLabel, smartlinkBtnLabel, onCreateLinks, onCreateSmartLinks, onOpenModal}) {
    if (isFormExpanded) {
        return null;
    }

    return (
        <div className="select-potok link-creation-actions">
            <button className="btn btn-outline-primary btn-margin select-potok"
                    onClick={onCreateLinks}>{offersBtnLabel}
            </button>
            <button className="btn btn-outline-success btn-margin select-potok"
                    onClick={onCreateSmartLinks}>{smartlinkBtnLabel}
            </button>
            <button className="btn btn-outline-warning btn-margin select-potok"
                    onClick={onOpenModal}>Проверить Postback
            </button>
        </div>
    );
}

function MobileLinkCreationActions({isFormExpanded, offersBtnLabel, smartlinkBtnLabel, onCreateLinks, onCreateSmartLinks, onOpenModal}) {
    if (isFormExpanded) {
        return null;
    }

    return (
        <div className="buttons-block btn-block link-creation-mobile-actions">
            <button className="btn btn-outline-primary"
                    onClick={onCreateLinks}>{offersBtnLabel}
            </button>
            <button className="btn btn-outline-success"
                    onClick={onCreateSmartLinks}>{smartlinkBtnLabel}</button>
            <button className="btn btn-outline-warning"
                onClick={onOpenModal}>Тест Postback
            </button>
        </div>
    );
}

function DesktopLinkCreationForm({
    domainsFilter,
    projectsFilter,
    billingTypeFilter,
    isFormExpanded,
    offersBtnLabel,
    smartlinkBtnLabel,
    returnButton,
    postbackModal,
    onCreateLinks,
    onCreateSmartLinks,
    onOpenModal,
}) {
    return (
        <div className="link-creation-form">
            <div className="link-creation-toolbar">
                <div className="link-creation-controls">
                    <div className="link-creation-control">
                        {domainsFilter}
                    </div>
                    <div className="link-creation-control">
                        {projectsFilter}
                    </div>
                    <div className="link-creation-control">
                        {billingTypeFilter}
                    </div>
                    <LinkCreationActions isFormExpanded={isFormExpanded}
                                         offersBtnLabel={offersBtnLabel}
                                         smartlinkBtnLabel={smartlinkBtnLabel}
                                         onCreateLinks={onCreateLinks}
                                         onCreateSmartLinks={onCreateSmartLinks}
                                         onOpenModal={onOpenModal}/>
                </div>
                <div className="link-creation-return">
                    {returnButton}
                </div>
            </div>
            {postbackModal}
        </div>
    );
}

function MobileLinkCreationForm({
    domainsFilter,
    projectsFilter,
    billingTypeFilter,
    isFormExpanded,
    offersBtnLabel,
    smartlinkBtnLabel,
    returnButton,
    postbackModal,
    onCreateLinks,
    onCreateSmartLinks,
    onOpenModal,
}) {
    return (
        <div className="link-creation-mobile-form">
            <div className="link-creation-mobile-return">
                {returnButton}
            </div>
            <div className="link-creation-mobile-control">
                {domainsFilter}
            </div>
            <div className="link-creation-mobile-control">
                {projectsFilter}
            </div>
            <div className="link-creation-mobile-control">
                {billingTypeFilter}
            </div>
            <MobileLinkCreationActions isFormExpanded={isFormExpanded}
                                       offersBtnLabel={offersBtnLabel}
                                       smartlinkBtnLabel={smartlinkBtnLabel}
                                       onCreateLinks={onCreateLinks}
                                       onCreateSmartLinks={onCreateSmartLinks}
                                       onOpenModal={onOpenModal}/>
            {postbackModal}
        </div>
    );
}

function LinkCreationContent({
    macrosPreset,
    macros,
    smartLinksTable,
    simpleLinksTable,
}) {
    return (
        <>
            <div className="padding-5-10">
                <MacrosCollapse macrosPreset={macrosPreset} macros={macros}/>
            </div>
            {smartLinksTable}
            {simpleLinksTable}
            <OfferLinkInfo/>
        </>
    );
}

function LinkCreationComponent({
    selectedOffers = [],
    returnToPrevComponent,
    selectedProject,
    onSelectProject,
    onExcludeOffer,
    onShowEditSmartLinkComponent,
    createdSmartLinksByProjectIdMap,
    smartLinksTemplates,
    deleteSmartLink,
    createLinks,
    createSmartLinks,
    shouldScrollToLink,
    shouldScrollToSmartLink,
    macros,
    selectedBillingType,
    onSelectBillingType,
}) {
    const dispatch = useDispatch();
    const isPostbackTestSending = useSelector(state => state.postbackTestReducer.sending);
    const projects = useSelector(state => state.projects.projects);
    const domains = useSelector(state => state.partnerDomainsReducer.partnerDomains);
    const macrosPreset = useSelector(state => state.preset.macrosPreset);
    const promotionLinks = useSelector(state => state.partnerOffers.promotionLinks);

    const containerRef = useRef(null);
    const needToSetDefaultProjectRef = useRef(true);
    const [isFormExpanded, setIsFormExpanded] = useState(false);
    const [width, setWidth] = useState(getInitialWidth);
    const [selectedDomain, setSelectedDomain] = useState(null);
    const [isOpenedModal, setIsOpenedModal] = useState(false);
    const [billingTypes, setBillingTypes] = useState([]);

    useLayoutEffect(() => {
        const handleResize = () => {
            setWidth(prevWidth => {
                const currentWidth = getContentWidth(containerRef.current);
                return currentWidth !== prevWidth ? currentWidth : prevWidth;
            });
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        dispatch(loadPartnerProjectsWithNew());
        const accessType = presetsAccessType();
        dispatch(loadUserPresets(PARTNER_MACROS, accessType, null, () => console.log("macros_loaded")));

        const offerIds = selectedOffers.map(offer => offer.offerId);
        dispatch(loadPromotionLinks(offerIds));
        window.scrollTo({top: 0});
        const billingTypeDropdown = buildBillingTypeDropdown(selectedOffers);
        setBillingTypes(billingTypeDropdown);
        onSelectBillingType(billingTypeDropdown[0]);
    }, []);

    useEffect(() => {
        if (domains && !selectedDomain) {
            const defaultDomain = domains.find(domain => domain.defaultDomain);
            setSelectedDomain(defaultDomain);
        }
    }, [domains, selectedDomain]);

    useEffect(() => {
        if (needToSetDefaultProjectRef.current && projects && projects.length > 1) {
            if (!selectedProject || selectedProject.id === -1) {
                onSelectProject(projects[1]);
            }
            needToSetDefaultProjectRef.current = false;
        }
    }, [onSelectProject, projects, selectedProject]);

    const selectProject = useCallback((project) => {
        const isExpanded = project && project.id === -1;
        onSelectProject(project);
        setIsFormExpanded(isExpanded);
    }, [onSelectProject]);

    const hideForm = useCallback(() => {
        selectProject(null);
    }, [selectProject]);

    const openModal = useCallback(() => {
        setIsOpenedModal(true)
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsOpenedModal(false);
        onSelectBillingType(null);
        selectProject(null);
    }, [onSelectBillingType, selectProject]);

    const onSaveProject = useCallback((project) => {
        dispatch(saveProject(project, () => null));
    }, [dispatch]);

    const onSendPostback = useCallback((postbackTest, handlePostbackTestResult) => {
        dispatch(sendPostbackTest(postbackTest, handlePostbackTestResult));
    }, [dispatch]);

    const createdSmartLinks = selectedProject && createdSmartLinksByProjectIdMap[selectedProject.id];
    const hasSelectedOffers = hasOffers(selectedOffers);

    const domainsFilter = domains && domains.length > 0
         && <MaterialUISelect
             styles={{root: {minWidth: 150}}}
             dropdownData={domains}
             displayedFields={['id', 'cname']} dataField='id'
             placeholder="Домен"
             selectedValue={selectedDomain}
             onSelectCallbackFunction={(domain) => setSelectedDomain(domain)}
             isSearchable={true}/>;

    const projectsFilter = hasSelectedOffers
        && <MaterialUISelect
            styles={{root: {minWidth: 150}}}
            dropdownData={projects}
            displayedFields={['sourceId', 'name']} dataField='id'
            placeholder="Поток"
            selectedValue={selectedProject}
            onSelectCallbackFunction={(project) => selectProject(project)}
            isSearchable={true}/>;

    const billingTypeFilter = hasSelectedOffers
        && <MaterialUISelect
            styles={{root: {minWidth: 150}}}
            dropdownData={billingTypes}
            displayedField='fullName' dataField='type'
            placeholder="Тип выплаты"
            selectedValue={selectedBillingType}
            onSelectCallbackFunction={(billingType) => onSelectBillingType(billingType)}
            isSearchable={true}/>;


    const smartlinkBtnLabel = smartLinksTemplates && smartLinksTemplates.length === 1 ? 'Создать смартлинк' : 'Создать смартлинки';
    const offersBtnLabel = "Получить ссылк" + (selectedOffers?.length === 1 ? "у" : "и");

    const returnButton = <ReturnButton onClick={returnToPrevComponent}/>;

    const smartLinksTable = (createdSmartLinks && createdSmartLinks.length !== 0)
        && <SmartLinksCreationTable smartLinks={createdSmartLinks} onDeleteSmartLink={deleteSmartLink}
                                    onShowEditSmartLinkComponent={onShowEditSmartLinkComponent}
                                    selectedDomain={selectedDomain}
                                    shouldScrollToSmartLink={shouldScrollToSmartLink}/>;
    const simpleLinksTable = <LinksCreationTable groupName="Офферы" offers={selectedOffers}
                                                 selectedProject={selectedProject}
                                                 excludeOffer={onExcludeOffer}
                                                 selectedDomain={selectedDomain}
                                                 shouldScrollToLink={shouldScrollToLink}
                                                 promotionLinks={promotionLinks}/>;


    const desktopPostbackModal =
        <PostbackTestModal isOpened={isOpenedModal}
                           isSending={isPostbackTestSending}
                           offers={selectedOffers}
                           projects={projects}
                           selectedProject={selectedProject}
                           onCloseModal={handleCloseModal}
                           onSendPostback={onSendPostback}
        />;

    const mobilePostbackModal =
        <PostbackTestModal isOpened={isOpenedModal}
                           isSending={isPostbackTestSending}
                           offers={selectedOffers}
                           projects={projects}
                           selectedProject={selectedProject}
                           onCloseModal={handleCloseModal}
                           onSaveProject={onSaveProject}
        />;

    const createLinksWithMacros = () => createLinks(true);
    const createSmartLinksFromTemplates = () => createSmartLinks(smartLinksTemplates);

    const desktopForm = <DesktopLinkCreationForm domainsFilter={domainsFilter}
                                                 projectsFilter={projectsFilter}
                                                 billingTypeFilter={billingTypeFilter}
                                                 isFormExpanded={isFormExpanded}
                                                 offersBtnLabel={offersBtnLabel}
                                                 smartlinkBtnLabel={smartlinkBtnLabel}
                                                 returnButton={returnButton}
                                                 postbackModal={desktopPostbackModal}
                                                 onCreateLinks={createLinksWithMacros}
                                                 onCreateSmartLinks={createSmartLinksFromTemplates}
                                                 onOpenModal={openModal}/>;

    const mobileForm = <MobileLinkCreationForm domainsFilter={domainsFilter}
                                               projectsFilter={projectsFilter}
                                               billingTypeFilter={billingTypeFilter}
                                               isFormExpanded={isFormExpanded}
                                               offersBtnLabel={offersBtnLabel}
                                               smartlinkBtnLabel={smartlinkBtnLabel}
                                               returnButton={returnButton}
                                               postbackModal={mobilePostbackModal}
                                               onCreateLinks={createLinksWithMacros}
                                               onCreateSmartLinks={createSmartLinksFromTemplates}
                                               onOpenModal={openModal}/>;

    return (
            <PageContent
                contentEl={
                    <div className="background-wht-blk link-creation">
                        {width > 659 ? desktopForm : mobileForm}
                        <ProjectCreationCollapse isExpanded={isFormExpanded}
                                                 onHideForm={() => hideForm()}
                                                 onSelectCreatedProject={selectProject}/>

                        <LinkCreationContent macrosPreset={macrosPreset}
                                             macros={macros}
                                             smartLinksTable={smartLinksTable}
                                             simpleLinksTable={simpleLinksTable}/>
                        <div ref={containerRef}/>
                    </div>
                }
            >
            </PageContent>
    );
}

LinkCreationComponent.propTypes = {
    selectedOffers: PropTypes.array,
    returnToPrevComponent: PropTypes.func,
    selectedProject: PropTypes.any,
    onSelectProject: PropTypes.func,
    onExcludeOffer: PropTypes.func,
    onShowEditSmartLinkComponent: PropTypes.func,
    createdSmartLinksByProjectIdMap: PropTypes.any,
    smartLinksTemplates: PropTypes.any,
    deleteSmartLink: PropTypes.func,
    createLinks: PropTypes.func,
    createSmartLinks: PropTypes.func,
    shouldScrollToLink: PropTypes.bool,
    shouldScrollToSmartLink: PropTypes.bool,
    macros: PropTypes.any,
    selectedBillingType: PropTypes.any,
    onSelectBillingType: PropTypes.func,
};

function hasOffers(selectedOffers) {
    return selectedOffers && selectedOffers.length > 0;
}

export default LinkCreationComponent;
